import React, { useState } from 'react';
import { Printer, Calendar, Filter } from 'lucide-react';

/**
 * PrintView – generates a printable comprehensive financial report with date range customization.
 *
 * Props
 *  ─ incomeData:       IncomeRow[]
 *  ─ netIncomeData:    NetIncRow[]
 *  ─ netWorth:         { assets, liabilities, netWorth, fireGoal, progress }
 *  ─ totalDebts:       number
 *  ─ goals:            Goal[]
 *  ─ investments:      Investment[]
 *  ─ totalInvestments: number
 */
const PrintView = ({
  incomeData,
  netIncomeData,
  netWorth,
  totalDebts,
  goals,
  investments,
  totalInvestments,
}) => {
  // Date range state
  const [reportDateRange, setReportDateRange] = useState('all');
  const [customStartMonth, setCustomStartMonth] = useState('');
  const [customEndMonth, setCustomEndMonth] = useState('');

  /* ───────────────────────── helper functions ─────────────────────────── */
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const getMonthIndex = (monthAbbr) => MONTHS.indexOf(monthAbbr);

  const getFilteredData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    switch (reportDateRange) {
      case 'last3':
        return data.slice(-3);
      case 'last6':
        return data.slice(-6);
      case 'last12':
        return data.slice(-12);
      case 'ytd':
        // Get current month and filter from January
        const currentYear = new Date().getFullYear();
        return data.filter(item => {
          // Assuming we're in the same year for YTD
          return true; // Simplified - in real app you'd check actual dates
        });
      case 'custom':
        if (!customStartMonth || !customEndMonth) return data;
        const startIdx = data.findIndex(item => item.month === customStartMonth);
        const endIdx = data.findIndex(item => item.month === customEndMonth);
        if (startIdx === -1 || endIdx === -1) return data;
        return data.slice(startIdx, endIdx + 1);
      case 'all':
      default:
        return data;
    }
  };

  // Get filtered data based on selected range
  const filteredIncomeData = getFilteredData(incomeData);
  const filteredNetIncomeData = getFilteredData(netIncomeData);

  // Get available months for custom range
  const availableMonths = incomeData.map(item => item.month);

  /* ───────────────────────── calculations ─────────────────────────────── */
  const currentMonth = filteredIncomeData[filteredIncomeData.length - 1] ?? { total: 0 };
  const previousMonth = filteredIncomeData[filteredIncomeData.length - 2] ?? null;
  const monthlyIncomeChange = previousMonth
    ? ((currentMonth.total - previousMonth.total) / previousMonth.total) * 100
    : 0;

  const totalAssets = netWorth.assets;
  const debtToAssetRatio = totalAssets > 0 ? (totalDebts / totalAssets) * 100 : 0;

  // Use filtered data for calculations
  const avgMonthlyIncome = filteredNetIncomeData.length > 0
    ? filteredNetIncomeData.reduce((s, m) => s + m.income, 0) / filteredNetIncomeData.length
    : 0;
  const avgMonthlyExpenses = filteredNetIncomeData.length > 0
    ? filteredNetIncomeData.reduce((s, m) => s + m.expenses, 0) / filteredNetIncomeData.length
    : 0;
  const avgNetIncome = avgMonthlyIncome - avgMonthlyExpenses;

  const timeToFIRE = avgNetIncome > 0
    ? Math.ceil((netWorth.fireGoal - netWorth.netWorth) / (avgNetIncome * 12))
    : 0;

  // Calculate period totals
  const periodTotalIncome = filteredNetIncomeData.reduce((s, m) => s + m.income, 0);
  const periodTotalExpenses = filteredNetIncomeData.reduce((s, m) => s + m.expenses, 0);
  const periodNetIncome = periodTotalIncome - periodTotalExpenses;

  /* ───────────────────────── reporting period label ─────────────────────── */
  const getReportingPeriodLabel = () => {
    const startMonth = filteredIncomeData[0]?.month || 'N/A';
    const endMonth = filteredIncomeData[filteredIncomeData.length - 1]?.month || 'N/A';
    
    switch (reportDateRange) {
      case 'last3':
        return `Last 3 Months (${startMonth} – ${endMonth})`;
      case 'last6':
        return `Last 6 Months (${startMonth} – ${endMonth})`;
      case 'last12':
        return `Last 12 Months (${startMonth} – ${endMonth})`;
      case 'ytd':
        return `Year to Date (Jan – ${endMonth})`;
      case 'custom':
        return `Custom Range (${startMonth} – ${endMonth})`;
      case 'all':
      default:
        return `All Available Data (${startMonth} – ${endMonth})`;
    }
  };

  /* ───────────────────────── print handler ─────────────────────────────── */
  const printData = () => window.print();

  /* ─────────────────────────── render ──────────────────────────────────── */
  return (
    <div className="space-y-6 print:space-y-4">
      {/* Top bar with date filters – hidden when printing */}
      <div className="no-print space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Comprehensive Financial Report</h3>
          <button
            onClick={printData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Printer size={16} /> Print Report
          </button>
        </div>

        {/* Date Range Controls */}
        <div className="bg-arc-elevated p-4 rounded-lg shadow-md">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={16} className="text-arc-muted" />
            <h4 className="font-semibold">Report Date Range</h4>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
            {[
              ['all', 'All Data'],
              ['last3', 'Last 3 Months'],
              ['last6', 'Last 6 Months'],
              ['last12', 'Last 12 Months'],
              ['ytd', 'Year to Date'],
              ['custom', 'Custom Range'],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setReportDateRange(value)}
                className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                  reportDateRange === value
                    ? 'bg-blue-600 text-white'
                    : 'bg-white/[0.05] text-arc-muted hover:bg-white/[0.08]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Custom Range Selectors */}
          {reportDateRange === 'custom' && (
            <div className="flex items-center gap-4 p-3 bg-white/[0.03] rounded-lg">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-arc-muted">From:</label>
                <select
                  value={customStartMonth}
                  onChange={(e) => setCustomStartMonth(e.target.value)}
                  className="px-3 py-1 border rounded text-sm"
                >
                  <option value="">Select month</option>
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-arc-muted">To:</label>
                <select
                  value={customEndMonth}
                  onChange={(e) => setCustomEndMonth(e.target.value)}
                  className="px-3 py-1 border rounded text-sm"
                >
                  <option value="">Select month</option>
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Period Summary */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Filter size={14} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Selected Period</span>
            </div>
            <p className="text-sm text-blue-700">{getReportingPeriodLabel()}</p>
            <p className="text-xs text-blue-600 mt-1">
              {filteredIncomeData.length} months of data • 
              Total Income: ${periodTotalIncome.toLocaleString()} • 
              Total Expenses: ${periodTotalExpenses.toLocaleString()} • 
              Net: ${periodNetIncome.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Actual report */}
      <div className="bg-arc-elevated print:bg-arc-elevated">
        {/* Header */}
        <div className="text-center mb-8 print:mb-6 border-b pb-6 print:pb-4">
          <h1 className="text-3xl print:text-2xl font-bold text-arc-ink mb-2">
            Personal Finance Report
          </h1>
          <div className="text-arc-muted text-sm">
            <p>
              Generated on{' '}
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
            <p className="font-medium text-blue-700">
              {getReportingPeriodLabel()}
            </p>
            <p className="text-xs mt-1">
              {filteredIncomeData.length} months of financial data analyzed
            </p>
          </div>
        </div>

        {/* Executive summary */}
        <div className="mb-8 print:mb-6">
          <h2 className="text-xl print:text-lg font-bold text-arc-ink mb-4 border-b pb-2">
            Executive Summary
          </h2>

          {/* Big metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              ['Net Worth', netWorth.netWorth, 'bg-blue-50', 'text-blue-600'],
              ['Total Assets', totalAssets, 'bg-green-50', 'text-green-600'],
              ['Total Debt', totalDebts, 'bg-red-50', 'text-red-600'],
              ['FIRE Progress', `${netWorth.progress.toFixed(1)}%`, 'bg-yellow-50', 'text-blue-600'],
            ].map(([label, val, bg, color]) => (
              <div key={label} className={`text-center p-4 print:p-3 border rounded ${bg} print:bg-white/[0.03]`}>
                <p className="text-sm text-arc-muted font-medium">{label}</p>
                <p className={`text-2xl print:text-xl font-bold ${color}`}>
                  {typeof val === 'number' ? `$${val.toLocaleString()}` : val}
                </p>
              </div>
            ))}
          </div>

          {/* Period-specific metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              ['Period Total Income', `$${periodTotalIncome.toLocaleString()}`],
              ['Period Total Expenses', `$${periodTotalExpenses.toLocaleString()}`],
              ['Period Net Income', `$${periodNetIncome.toLocaleString()}`],
              ['Avg Monthly Savings', `$${avgNetIncome.toLocaleString()}`],
            ].map(([label, val]) => (
              <div key={label} className="text-center p-3 print:p-2 border rounded bg-white/[0.03]">
                <p className="text-xs text-arc-muted font-medium">{label}</p>
                <p className="font-bold text-lg">{val}</p>
              </div>
            ))}
          </div>

          {/* Key derived metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {[
              ['Debt-to-Asset Ratio', `${debtToAssetRatio.toFixed(1)}%`],
              ['Avg Monthly Income', `$${avgMonthlyIncome.toLocaleString()}`],
              ['Savings Rate', avgMonthlyIncome > 0 ? `${((avgNetIncome / avgMonthlyIncome) * 100).toFixed(1)}%` : 'N/A'],
              ['Est. Years to FIRE', timeToFIRE > 0 ? timeToFIRE : 'N/A'],
            ].map(([label, val]) => (
              <div key={label} className="text-center p-3 print:p-2 border rounded">
                <p className="text-arc-muted">{label}</p>
                <p className="font-bold">{val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Period Performance Table */}
        <div className="mb-8 print:mb-6">
          <h2 className="text-xl print:text-lg font-bold text-arc-ink mb-4 border-b pb-2">
            Monthly Performance Overview
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border">
              <thead>
                <tr className="bg-white/[0.03]">
                  <th className="border p-2 text-left">Month</th>
                  <th className="border p-2 text-right">Income</th>
                  <th className="border p-2 text-right">Expenses</th>
                  <th className="border p-2 text-right">Net Income</th>
                  <th className="border p-2 text-right">Savings Rate</th>
                </tr>
              </thead>
              <tbody>
                {filteredNetIncomeData.map((month, idx) => {
                  const savingsRate = month.income > 0 ? ((month.netIncome / month.income) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                      <td className="border p-2 font-medium">{month.month}</td>
                      <td className="border p-2 text-right">${month.income.toLocaleString()}</td>
                      <td className="border p-2 text-right">${month.expenses.toLocaleString()}</td>
                      <td className={`border p-2 text-right font-medium ${month.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${month.netIncome.toLocaleString()}
                      </td>
                      <td className="border p-2 text-right">{savingsRate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Goals + Investments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 print:mb-6">
          {/* Goals */}
          <div>
            <h2 className="text-xl print:text-lg font-bold text-arc-ink mb-4 border-b pb-2">
              Financial Goals Progress
            </h2>
            {goals.map((g) => (
              <div key={g.name} className="mb-4 p-4 print:p-3 border rounded">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">{g.name}</span>
                  <span className="text-lg font-bold text-blue-600">{g.progress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-white/[0.08] rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full print:bg-white/10"
                    style={{ width: `${Math.min(g.progress, 100)}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {[
                    ['Current', g.current],
                    ['Target', g.target],
                    ['Remaining', Math.max(0, g.target - g.current)],
                  ].map(([label, val]) => (
                    <div key={label} className="text-center">
                      <p className="text-arc-muted">{label}</p>
                      <p className="font-semibold">${val.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Investments */}
          <div>
            <h2 className="text-xl print:text-lg font-bold text-arc-ink mb-4 border-b pb-2">
              Investment Portfolio
            </h2>
            <div className="space-y-2 mb-4">
              {investments.map((inv) => {
                const pct = totalInvestments > 0 ? (inv.amount / totalInvestments) * 100 : 0;
                return (
                  <div key={inv.name} className="flex justify-between items-center p-3 print:p-2 border rounded">
                    <div>
                      <p className="font-medium text-sm">{inv.name}</p>
                      <p className="text-xs text-arc-muted">{inv.type}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${inv.amount.toLocaleString()}</p>
                      <p className="text-xs text-arc-muted">{pct.toFixed(1)}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center p-3 bg-blue-50 print:bg-white/[0.03] rounded border">
              <p className="text-sm text-arc-muted">Total Portfolio Value</p>
              <p className="text-xl font-bold text-blue-600">${totalInvestments.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-arc-dim mt-8 pt-4 border-t">
          <p>
            This report is generated for informational purposes only and should not be considered
            financial advice.
          </p>
          <p>Consult qualified financial advisors for personalised planning.</p>
          <p className="mt-2">ARCFIRETracker – Personal Finance Dashboard</p>
        </div>
      </div>
    </div>
  );
};

export default PrintView;